import { useRequest } from 'ahooks';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon } from 'tdesign-icons-react';
import { MessagePlugin } from 'tdesign-react';

import { PIC_NAME, PIC_PRIVATE, PUBLIC, PUBLIC_NAME } from '@/const/library';
import { SEARCH_RELEASE } from '@/const/search';
import { IStory } from '@/interface';
import { LibraryObjectData, ResourceTabType } from '@/interface/library';
import { CgiErrorCode } from '@/services/cgi';
import { fetchStoryList } from '@/services/story';
import { isPic, isWoa } from '@/utils/getApiDomain';

import styles from './index.module.less';
import { InteractionList } from './scroll';

const size = 9999;

const defaultItem = [{
  label: PUBLIC_NAME,
  value: PUBLIC,
}];

const picItem = [
  {
    label: PUBLIC_NAME,
    value: PUBLIC,
  },
  {
    label: PIC_NAME,
    value: PIC_PRIVATE,
  },
];

const HeaderPanel: React.FC<{
  onChange: (material: {
    value: string
    label: string
  }) => void;
  value: LibraryObjectData;
  storyId: string | null
}> = (props) => {
  const { value, storyId } = props;
  const [open, setOpen] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [firstLoading, setFirstLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const release = searchParams.get(SEARCH_RELEASE) === 'true';

  const [page] = useState(1);
  const [list, setList] = useState<{
    label: string,
    value: string
  }[]>([]);

  const { runAsync: getList } = useRequest(fetchStoryList, { manual: true });

  useEffect(() => {
    setFirstLoading(true);
    getList({ size, page, release }).then((res) => {
      const { list: storyList, pageInfo, code, message } = res || {};
      if (code !== CgiErrorCode.NO_ERROR) {
        MessagePlugin.error(message || '获取故事列表失败，请重试');
        return;
      }
      const optionList = transList(storyList);
      const item = isPic ? picItem : defaultItem;
      const newList = list.concat(item).concat(optionList);
      const has_next = newList?.length < pageInfo?.total;
      setList(newList);
      setHasNext(has_next);
    });
  }, []);

  const transList = (list: IStory[]) => list.map(item => ({
    value: item?.id,
    label: item?.name || '未命名',
  }));

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleWindowClick);
    return () => {
      document.removeEventListener('click', handleWindowClick);
    };
  }, []);

  const handleWindowClick = () => {
    handleClose();
  };

  const handleClickContent = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const useList: {
    label: string;
    value: string;
  }[] = useMemo(() => {
    if (isPic) {
      return picItem;
    }

    if (isWoa || !storyId) {
      return [defaultItem[0]];
    }
    const currentItem = list.find(item => item.value === storyId);
    return [defaultItem[0], currentItem].filter(item => item !== undefined) as unknown as {
      label: string;
      value: string;
    }[];
  }, [list?.length, storyId]);

  const name = useMemo(() => {
    if (value?.tabType === ResourceTabType.TAB_TYPE_NOVA) {
      return PUBLIC_NAME;
    }
    if (value?.tabType === ResourceTabType.TAB_TYPE_PIC) {
      return PIC_NAME;
    }
    return useList?.find(e => e.value !== PUBLIC)?.label || '未命名';
  }, [useList, value?.tabType]);

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.title}>素材库</div>
        <div className={styles.split}></div>
        <div className={styles.storyName} onClick={handleClickContent}>
          <div className={styles.name}>{name}</div>
          <div className={classNames(
            styles.icon,
            { [styles.arrowFlipped]: open },
            { [styles.arrow]: !open },
          )} onClick={handleClick}>
            <ChevronDownIcon size={24} />
          </div>
          <div className={styles.list}>
            {open
              && <InteractionList
                list={useList}
                getNextPage={() => { }}
                firstLoading={firstLoading}
                hasNext={hasNext}
                onClose={handleClose}
                {...props} />}
          </div>
        </div>
      </div>
    </div>
  );
};


export default HeaderPanel;
